import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import { BackButton, CancelButton, SubmitButton } from "../../../../Components/Utils/Buttons";
import { NuInput, NuTextArea } from "../../../../Components/Utils/Input";
import FormikSelectTwo from "../../../../Components/Utils/Select";
import Breadcrumb from "../../../../Components/Utils/Breadcrumb";
import UseEditData from "../../../../Components/Hooks/useEditData";
import UseDropDown from "../../../../Components/Hooks/useDropDown";
import UseFetchCurrency from "../../../../Components/Hooks/useFetchCurrencyData";
import useFetchMataData from "../../../../Components/Hooks/useFetchMataData";
import NuAmount from "../../../../Components/Utils/NuAmount";
import axiosInstance from "../../../../Services/axiosCommon";
import Select from "react-select";
import AsyncSelect from "react-select/async";

const EditProformaInvoice = () => {
  const { piEditId } = useParams();
  const location = useLocation();
  const stateData = location.state?.data;
  const fromUrl = location.state?.from || "/sales/proforma-invoice";
  const pageIndex = location.state?.pageIndex;
  const searchQuery = location.state?.searchQuery;

  const { editData, isLoading, Oncancel } = UseEditData();

  // Async dropdown states
  const [selectedCustomer, setSelectedCustomer] = useState(
    stateData?.customerId
      ? {
        value: stateData.customerId,
        label: stateData.customerName || stateData.customer?.name || `Customer #${stateData.customerId}`,
      }
      : null
  );
  const [selectedBank, setSelectedBank] = useState(
    stateData?.bankId
      ? {
        value: stateData.bankId,
        label: stateData.bankName
          ? (stateData.accountNumber ? `${stateData.bankName} - ${stateData.accountNumber}` : stateData.bankName)
          : (stateData.bank?.bankName ? (stateData.bank?.accountNumber ? `${stateData.bank.bankName} - ${stateData.bank.accountNumber}` : stateData.bank.bankName) : `Bank #${stateData.bankId}`),
      }
      : null
  );

  // Dropdown hooks
  const { value: currencyOptions } = UseFetchCurrency("/currency/drop-down");

  // Async search loader for customers
  const loadCustomerOptions = useCallback((inputValue, callback) => {
    axiosInstance
      .get("/customers/drop-down", {
        params: { search: inputValue ? inputValue.trim() : "" },
      })
      .then((res) => {
        const raw = Array.isArray(res.data) ? res.data : res.data?.data || [];
        const formatted = raw.map((item) => ({
          ...item,
          value: item.id !== undefined ? item.id : item.value,
          label: item.name || item.customerName || item.label || `Customer #${item.id}`,
        }));
        callback(formatted);
      })
      .catch((err) => {
        console.error("Error fetching customer options:", err);
        callback([]);
      });
  }, []);

  // Async search loader for bank accounts
  const loadBankOptions = useCallback((inputValue, callback) => {
    axiosInstance
      .get("/banks/drop-down", {
        params: { search: inputValue ? inputValue.trim() : "" },
      })
      .then((res) => {
        const raw = Array.isArray(res.data) ? res.data : res.data?.data || [];
        const formatted = raw.map((item) => {
          const label = item.name || (item.bankName ? (item.accountNumber ? `${item.bankName} - ${item.accountNumber}` : item.bankName) : (item.label || item.title || item.code || String(item.id || "")));
          return {
            ...item,
            value: item.id !== undefined ? item.id : item.value,
            label: label || `Bank #${item.id}`,
          };
        });
        callback(formatted);
      })
      .catch((err) => {
        console.error("Error fetching bank options:", err);
        callback([]);
      });
  }, []);

  // Async search loader for products
  const loadProductOptions = useCallback((inputValue, callback) => {
    axiosInstance
      .get("/items/drop-down", {
        params: { search: inputValue ? inputValue.trim() : "" },
      })
      .then((res) => {
        const raw = Array.isArray(res.data) ? res.data : res.data?.data || [];
        const formatted = raw.map((item) => ({
          value: item.id,
          label: item.name,
          unit: item.unit,
        }));
        callback(formatted);
      })
      .catch((err) => {
        console.error("Error fetching product options:", err);
        callback([]);
      });
  }, []);

  const asyncSelectStyles = {
    control: (provided) => ({
      ...provided,
      border: "1px solid #e5e5e5",
      outline: "none",
      borderRadius: "10px",
      padding: "1px 0",
      color: "#000",
      textAlign: "left",
      fontWeight: "500",
      width: "100%",
      minHeight: "38px",
      backgroundColor: "#fff",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#cbd5e1",
      },
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 99999,
    }),
    option: (provided, state) => ({
      ...provided,
      padding: "8px 12px",
      fontSize: "12px",
      color: state.isSelected ? "#fff" : "#000",
      backgroundColor: state.isSelected ? "#2563EB" : state.isFocused ? "#eff6ff" : "#fff",
      ":hover": {
        color: "#fff",
        backgroundColor: "#60A5FA",
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      fontSize: "12px",
      color: "#000",
      fontWeight: "500",
    }),
    placeholder: (provided) => ({
      ...provided,
      fontSize: "12px",
      color: "#9ca3af",
      fontWeight: "500",
    }),
  };

  // Metadata dropdowns
  const { value: paymentTermsOptions } = useFetchMataData(
    "/dropdown/dropdown-details/",
    "PaymentTerms_Supplierqoutes"
  );
  const { value: deliveryTermsOptions } = useFetchMataData(
    "/dropdown/dropdown-details/",
    "DeliveryTerms_Supplierqoutes"
  );
  const { value: leadTimeOptions } = useFetchMataData(
    "/dropdown/dropdown-details/",
    "LeadTime_Supplierqoutes"
  );
  const { value: validityOptions } = useFetchMataData(
    "/dropdown/dropdown-details/",
    "Validity_Supplierquotes"
  );
  const { value: unitOptions } = useFetchMataData(
    "/dropdown/dropdown-details/",
    "Unit_item"
  );

  // Dynamic VAT rate from API
  const [vatRate, setVatRate] = useState(15);

  useEffect(() => {
    axiosInstance
      .get("/vat/")
      .then((res) => {
        const rate = res.data?.data?.vatRate ?? res.data?.vatRate;
        if (rate !== undefined && rate !== null && !isNaN(Number(rate))) {
          setVatRate(Number(rate));
        }
      })
      .catch((err) => {
        console.error("Error fetching VAT rate:", err);
      });
  }, []);

  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [dataLoading, setDataLoading] = useState(!stateData);
  const [products, setProducts] = useState([]);

  const [formValues, setFormValues] = useState({
    customerId: stateData?.customerId || "",
    customerAddressId: stateData?.customerAddressId || "",
    bankId: stateData?.bankId || "",
    date: stateData?.date || "",
    currencyId: stateData?.currencyId || "",
    paymentTerms: stateData?.paymentTerms || "100% Advance",
    deliveryTerms: stateData?.deliveryTerms || "",
    deliveryLeadTime: stateData?.deliveryLeadTime || "",
    validity: stateData?.validity || "",
    prId: stateData?.prId || null,
    prRef: stateData?.prRef || stateData?.prNumber || stateData?.pr?.prNumber || stateData?.pr?.ref || "",
    cqId: stateData?.cqId || null,
    cqRef: stateData?.cqRef || stateData?.cqNumber || stateData?.customerQuotationRef || stateData?.cqName || stateData?.customerQuotation?.name || stateData?.customerQuotation?.ref || "",
    customerPoId: stateData?.customerPoId || null,
    customerPoNo:
      stateData?.customerPoNo ||
      stateData?.customerPoNumber ||
      stateData?.customer_po_no ||
      stateData?.customerPo?.customerPoNo ||
      stateData?.customerPo?.poNumber ||
      stateData?.customerPo?.poNo ||
      "",
    vatApplicable: stateData?.vatApplicable !== undefined ? Boolean(stateData.vatApplicable) : false,
    discountAmount: stateData?.discountAmount || 0,
    transportationCharges: stateData?.transportationCharges || 0,
    remarks: stateData?.remarks || "",
  });

  // Fetch single PI details if not in state
  useEffect(() => {
    const fetchPiDetails = async () => {
      try {
        setDataLoading(true);
        const res = await axiosInstance.get(`/proforma-invoice/${piEditId}`);
        const pi = res.data?.data || res.data;
        if (pi) {
          setFormValues({
            customerId: pi.customerId || "",
            customerAddressId: pi.customerAddressId || "",
            bankId: pi.bankId || "",
            date: pi.date || "",
            currencyId: pi.currencyId || "",
            paymentTerms: pi.paymentTerms || "",
            deliveryTerms: pi.deliveryTerms || "",
            deliveryLeadTime: pi.deliveryLeadTime || "",
            validity: pi.validity || "",
            prId: pi.prId || null,
            prRef: pi.prRef || pi.prNumber || pi.pr?.prNumber || pi.pr?.ref || "",
            cqId: pi.cqId || null,
            cqRef: pi.cqRef || pi.cqNumber || pi.customerQuotationRef || pi.cqName || pi.customerQuotation?.name || pi.customerQuotation?.ref || "",
            customerPoId: pi.customerPoId || null,
            customerPoNo:
              pi.customerPoNo ||
              pi.customerPoNumber ||
              pi.customer_po_no ||
              pi.customerPo?.customerPoNo ||
              pi.customerPo?.poNumber ||
              pi.customerPo?.poNo ||
              "",
            vatApplicable: pi.vatApplicable !== undefined ? Boolean(pi.vatApplicable) : false,
            discountAmount: pi.discountAmount || 0,
            transportationCharges: pi.transportationCharges || 0,
            remarks: pi.remarks || "",
          });

          if (pi.customerId) {
            setSelectedCustomer({
              value: pi.customerId,
              label: pi.customerName || pi.customer?.name || pi.customer?.customerName || `Customer #${pi.customerId}`,
            });
          }
          if (pi.bankId) {
            const bLabel = pi.bankName
              ? (pi.accountNumber ? `${pi.bankName} - ${pi.accountNumber}` : pi.bankName)
              : (pi.bank?.bankName ? (pi.bank?.accountNumber ? `${pi.bank.bankName} - ${pi.bank.accountNumber}` : pi.bank.bankName) : (pi.bankAccountName || `Bank #${pi.bankId}`));
            setSelectedBank({
              value: pi.bankId,
              label: bLabel,
            });
          }

          const prodsArray = pi.piProducts || pi.products || pi.pi_products;
          if (prodsArray && Array.isArray(prodsArray) && prodsArray.length > 0) {
            const mapped = prodsArray.map((p) => ({
              productId: p.productId || p.itemId || p.id || "",
              productName:
                p.product_name ||
                p.productName ||
                p.product?.name ||
                p.product?.productName ||
                p.name ||
                "",
              quantity: p.quantity ?? 1,
              unitPrice: p.unitPrice ?? p.price ?? 0,
              unit: p.unit || p.product_unit || p.productUnit || "",
              notes: p.notes || "",
            }));
            setProducts(mapped);
          } else {
            setProducts([{ productId: "", quantity: 1, unitPrice: 0, unit: "", notes: "" }]);
          }
        }
      } catch (err) {
        console.error("Error fetching PI:", err);
      } finally {
        setDataLoading(false);
      }
    };

    if (piEditId) {
      fetchPiDetails();
    }
  }, [piEditId]);

  // Fetch customer addresses when customer is selected
  useEffect(() => {
    if (formValues.customerId) {
      axiosInstance
        .get(`/customers/${formValues.customerId}`)
        .then((res) => {
          const custData = res.data?.data || res.data;
          if (custData?.addresses && Array.isArray(custData.addresses)) {
            const addrs = custData.addresses.map((a) => ({
              value: a.id,
              label: `${a.address1 || a.streetName || ""} ${a.city || ""} ${a.country || ""}`.trim() || `Address #${a.id}`,
            }));
            setCustomerAddresses(addrs);
          } else {
            setCustomerAddresses([]);
          }
        })
        .catch((err) => console.log("Customer fetch err:", err));
    } else {
      setCustomerAddresses([]);
    }
  }, [formValues.customerId]);

  // Product line item handlers
  const handleAddProductRow = () => {
    setProducts((prev) => [
      ...prev,
      { productId: "", quantity: 1, unitPrice: 0, unit: "", notes: "" },
    ]);
  };

  const handleRemoveProductRow = (index) => {
    if (products.length === 1) return;
    setProducts((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleProductChange = (index, field, value) => {
    setProducts((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Calculations
  const subTotal = useMemo(() => {
    return products.reduce((acc, item) => {
      const qty = Number(item.quantity) || 0;
      const price = Number(item.unitPrice) || 0;
      return acc + qty * price;
    }, 0);
  }, [products]);

  const discount = Number(formValues.discountAmount) || 0;
  const transportation = Number(formValues.transportationCharges) || 0;
  const taxableAmount = Math.max(0, subTotal - discount + transportation);
  const vatAmount = formValues.vatApplicable ? taxableAmount * (vatRate / 100) : 0;
  const grandTotal = taxableAmount + vatAmount;

  const validationSchema = yup.object({
    customerId: yup.string().required("Customer is required"),
    bankId: yup.string().required("Bank is required"),
    date: yup.string().required("Date is required"),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: formValues,
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values, { resetForm }) => {
      const validProducts = products.filter(
        (p) => p.productId && Number(p.quantity) > 0
      );

      if (validProducts.length === 0) {
        alert("Please add at least one valid product item.");
        return;
      }

      const payload = {
        customerId: Number(values.customerId),
        bankId: Number(values.bankId),
        date: values.date,
        currencyId: values.currencyId ? Number(values.currencyId) : null,
        paymentTerms: values.paymentTerms || "",
        deliveryTerms: values.deliveryTerms || "",
        deliveryLeadTime: values.deliveryLeadTime || "",
        validity: values.validity || "",
        prId: values.prId ? Number(values.prId) : null,
        cqId: values.cqId ? Number(values.cqId) : null,
        vatApplicable: Boolean(values.vatApplicable),
        discountAmount: Number(values.discountAmount) || 0,
        transportationCharges: Number(values.transportationCharges) || 0,
        remarks: values.remarks || "",
        products: validProducts.map((p) => ({
          productId: Number(p.productId),
          quantity: Number(p.quantity),
          unitPrice: Number(p.unitPrice) || 0,
          unit: p.unit || "",
        })),
      };

      if (values.customerAddressId) {
        payload.customerAddressId = Number(values.customerAddressId);
      }

      editData(
        `/proforma-invoice/edit-pi/${piEditId}`,
        payload,
        resetForm,
        fromUrl,
        { currentPage: pageIndex, searchQuery }
      );
    },
  });

  const selectedCurrency = useMemo(() => {
    const selectedId = formik.values.currencyId || formValues.currencyId;
    const found = (currencyOptions || []).find(
      (c) => String(c.value) === String(selectedId)
    );
    return found?.label || "SAR";
  }, [formik.values.currencyId, formValues.currencyId, currencyOptions]);

  if (dataLoading) {
    return (
      <div className="w-full h-full py-8 px-4 flex flex-col items-center justify-center">
        <div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mb-3"></div>
        <p className="text-sm font-medium text-gray-500">Loading Proforma Invoice Details...</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full py-2 px-4">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-2">
        <Breadcrumb
          items={[
            { name: "Sales", link: "/sales/proforma-invoice" },
            { name: "Proforma Invoice", link: "/sales/proforma-invoice" },
            { name: "Edit" },
          ]}
        />
        <BackButton />
      </div>

      {/* Main Form Container */}
      <form onSubmit={formik.handleSubmit}>
        <div className="w-full mx-auto md:h-[calc(100vh-145px)] bg-white rounded-md mt-4 overflow-hidden flex flex-col justify-between">
          <div className="w-full p-4 sm:p-6 overflow-y-auto space-y-6">

            {/* Section 1: Basic Information */}
            <div>
              <div className="border-b border-gray-100 pb-2 mb-4">
                <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                  Primary Details & Parties
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Customer with Async Search */}
                <div className="w-full relative px-1 py-2">
                  <label className="text-xs 2xl:text-sm">
                    Customer <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <AsyncSelect
                      cacheOptions
                      defaultOptions
                      loadOptions={loadCustomerOptions}
                      value={selectedCustomer}
                      onChange={(opt) => {
                        setSelectedCustomer(opt);
                        const val = opt?.value !== undefined && opt?.value !== null ? String(opt.value) : "";
                        formik.setFieldValue("customerId", val);
                        setFormValues((prev) => ({ ...prev, customerId: val }));
                      }}
                      placeholder="Select Customer"
                      isClearable={true}
                      className="text-xs 2xl:text-sm mt-2 font-medium"
                      menuPlacement="auto"
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      styles={asyncSelectStyles}
                    />
                  </div>
                  {formik.errors.customerId && (
                    <span className="text-xs font-semibold text-red-500">
                      {formik.errors.customerId}
                    </span>
                  )}
                </div>

                {customerAddresses.length > 0 && (
                  <FormikSelectTwo
                    label="Customer Address"
                    placeholder="Select Address"
                    Options={customerAddresses}
                    formik={formik}
                    name="customerAddressId"
                    width="w-full"
                  />
                )}

                {/* Bank Account with Async Search */}
                <div className="w-full relative px-1 py-2">
                  <label className="text-xs 2xl:text-sm">
                    Bank Account <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <AsyncSelect
                      cacheOptions
                      defaultOptions
                      loadOptions={loadBankOptions}
                      value={selectedBank}
                      onChange={(opt) => {
                        setSelectedBank(opt);
                        const val = opt?.value !== undefined && opt?.value !== null ? String(opt.value) : "";
                        formik.setFieldValue("bankId", val);
                        setFormValues((prev) => ({ ...prev, bankId: val }));
                      }}
                      placeholder="Select Bank"
                      isClearable={true}
                      className="text-xs 2xl:text-sm mt-2 font-medium"
                      menuPlacement="auto"
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      styles={asyncSelectStyles}
                    />
                  </div>
                  {formik.errors.bankId && (
                    <span className="text-xs font-semibold text-red-500">
                      {formik.errors.bankId}
                    </span>
                  )}
                </div>

                <FormikSelectTwo
                  label="Currency"
                  placeholder="Select Currency"
                  Options={currencyOptions}
                  formik={formik}
                  name="currencyId"
                  width="w-full"
                  onChange={(opt) => {
                    formik.setFieldValue("currencyId", opt?.value || "");
                    setFormValues((prev) => ({ ...prev, currencyId: opt?.value || "" }));
                  }}
                />

                <NuInput
                  label="Date"
                  type="date"
                  formik={formik}
                  name="date"
                  isRequired={true}
                  width="w-full"
                />

                {formik.values.prRef && (
                  <NuInput
                    label="PR Ref"
                    formik={formik}
                    name="prRef"
                    disabled={true}
                    width="w-full"
                  />
                )}

                {formik.values.cqRef && (
                  <NuInput
                    label="CQ Ref"
                    formik={formik}
                    name="cqRef"
                    disabled={true}
                    width="w-full"
                  />
                )}

                {!formik.values.prRef && !formik.values.cqRef && (formik.values.customerPoId || formik.values.customerPoNo) && (
                  <NuInput
                    label="Customer PO No."
                    placeholder="e.g. PO-12345"
                    formik={formik}
                    name="customerPoNo"
                    disabled={true}
                    width="w-full"
                  />
                )}
              </div>
            </div>

            {/* Section 2: Commercial Terms */}
            <div>
              <div className="border-b border-gray-100 pb-2 mb-4">
                <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                  Commercial Terms
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <FormikSelectTwo
                  label="Payment Terms"
                  placeholder="Select Terms"
                  Options={paymentTermsOptions}
                  formik={formik}
                  name="paymentTerms"
                  width="w-full"
                />

                <FormikSelectTwo
                  label="Delivery Terms"
                  placeholder="Select Terms"
                  Options={deliveryTermsOptions}
                  formik={formik}
                  name="deliveryTerms"
                  width="w-full"
                />

                <FormikSelectTwo
                  label="Delivery Lead Time"
                  placeholder="Select Lead Time"
                  Options={leadTimeOptions}
                  formik={formik}
                  name="deliveryLeadTime"
                  width="w-full"
                />

                <FormikSelectTwo
                  label="Validity"
                  placeholder="Select Validity"
                  Options={validityOptions}
                  formik={formik}
                  name="validity"
                  width="w-full"
                />
              </div>
            </div>

            {/* Section 3: Product Line Items Table */}
            <div>
              <div className="flex justify-between items-center border-b border-gray-100 pb-2 mb-4">
                <div>
                  <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
                    Products
                  </h3>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Update products, quantities, unit prices, and line item units.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleAddProductRow}
                  className="px-3 py-1.5 bg-[#048178] hover:bg-[#036961] text-white text-xs font-semibold rounded-lg flex items-center gap-1 transition-colors cursor-pointer"
                >
                  <span>+</span> Add Product
                </button>
              </div>

              <div className="overflow-x-auto border border-gary rounded-lg bg-white">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-baseColor-primary">
                    <tr className="border-b border-gray-200 text-xs font-medium text-white">
                      <th className="p-3 w-12 text-center text-white font-medium">S.No</th>
                      <th className="p-3 min-w-[220px] text-white font-medium">Product / Item</th>
                      <th className="p-3 min-w-[130px] w-36 text-white font-medium">Unit</th>

                      <th className="p-3 w-28 text-white font-medium">Quantity</th>
                      <th className="p-3 w-36 text-white font-medium">Unit Price</th>
                      <th className="p-3 w-32 text-right text-white font-medium">Total</th>
                      <th className="p-3 w-14 text-center text-white font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 text-xs">
                    {products.map((item, idx) => {
                      const lineTotal = (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0);
                      return (
                        <tr key={idx} className="bg-white hover:bg-gray-50/50">
                          <td className="p-3 text-center font-semibold text-gray-400">{idx + 1}</td>

                          {/* Product Selection */}
                          <td className="p-3">
                            <AsyncSelect
                              cacheOptions
                              defaultOptions
                              loadOptions={loadProductOptions}
                              value={
                                item.productId
                                  ? {
                                    value: item.productId,
                                    label: item.productName || `Item #${item.productId}`,
                                  }
                                  : null
                              }
                              onChange={(opt) => {
                                if (opt) {
                                  handleProductChange(idx, "productId", opt.value);
                                  handleProductChange(idx, "productName", opt.label);
                                  if (opt.unit) {
                                    handleProductChange(idx, "unit", opt.unit);
                                  }
                                } else {
                                  handleProductChange(idx, "productId", "");
                                  handleProductChange(idx, "productName", "");
                                }
                              }}
                              placeholder="Select product..."
                              isClearable={true}
                              className="text-xs font-medium"
                              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                              menuPosition="fixed"
                              styles={{
                                control: (provided) => ({
                                  ...provided,
                                  minHeight: "36px",
                                  borderRadius: "8px",
                                  borderColor: "#e5e7eb",
                                  fontSize: "12px",
                                }),
                                option: (provided, state) => ({
                                  ...provided,
                                  fontSize: "12px",
                                  padding: "7px 10px",
                                  color: state.isSelected ? "#fff" : "#1f2937",
                                  backgroundColor: state.isSelected ? "#048178" : state.isFocused ? "#f0fdfa" : "#fff",
                                  ":hover": {
                                    backgroundColor: state.isSelected ? "#048178" : "#f0fdfa",
                                    color: state.isSelected ? "#fff" : "#0f766e",
                                  },
                                }),
                                singleValue: (provided) => ({
                                  ...provided,
                                  fontSize: "12px",
                                }),
                                placeholder: (provided) => ({
                                  ...provided,
                                  fontSize: "12px",
                                }),
                                menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                              }}
                            />
                          </td>
                          {/* Unit Dropdown */}
                          <td className="p-3">
                            <Select
                              options={unitOptions || []}
                              value={
                                (unitOptions || []).find((u) => u.value === item.unit || u.label === item.unit) ||
                                (item.unit ? { label: item.unit, value: item.unit } : null)
                              }
                              onChange={(opt) => handleProductChange(idx, "unit", opt?.label || opt?.value || "")}
                              placeholder="Select..."
                              className="text-xs font-medium"
                              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                              menuPosition="fixed"
                              styles={{
                                control: (provided) => ({
                                  ...provided,
                                  minHeight: "36px",
                                  borderRadius: "8px",
                                  borderColor: "#e5e7eb",
                                  fontSize: "12px",
                                }),
                                option: (provided, state) => ({
                                  ...provided,
                                  fontSize: "12px",
                                  padding: "7px 10px",
                                  color: state.isSelected ? "#fff" : "#1f2937",
                                  backgroundColor: state.isSelected ? "#048178" : state.isFocused ? "#f0fdfa" : "#fff",
                                  ":hover": {
                                    backgroundColor: state.isSelected ? "#048178" : "#f0fdfa",
                                    color: state.isSelected ? "#fff" : "#0f766e",
                                  },
                                }),
                                singleValue: (provided) => ({
                                  ...provided,
                                  fontSize: "12px",
                                }),
                                placeholder: (provided) => ({
                                  ...provided,
                                  fontSize: "12px",
                                }),
                                menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                              }}
                            />
                          </td>
                          {/* Quantity */}
                          <td className="p-3">
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => handleProductChange(idx, "quantity", e.target.value)}
                              className="w-full h-9 px-2 text-xs font-medium border border-gray-200 rounded-lg outline-none focus:border-emerald-500"
                            />
                          </td>

                          {/* Unit Price */}
                          <td className="p-3">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={item.unitPrice}
                              onChange={(e) => handleProductChange(idx, "unitPrice", e.target.value)}
                              className="w-full h-9 px-2 text-xs font-medium border border-gray-200 rounded-lg outline-none focus:border-emerald-500"
                            />
                          </td>



                          {/* Line Total */}
                          <td className="p-3 text-right font-bold text-gray-800">
                            <NuAmount currency={selectedCurrency} value={lineTotal} />
                          </td>

                          {/* Delete Row */}
                          <td className="p-3 text-center">
                            <button
                              type="button"
                              onClick={() => handleRemoveProductRow(idx)}
                              disabled={products.length === 1}
                              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30 cursor-pointer"
                              title="Delete Row"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Section 4: Remarks & Financial Calculations */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 pt-2">

              {/* Left Column: Remarks */}
              <div className="lg:col-span-7 space-y-4">
                <NuTextArea
                  label="Remarks & Notes"
                  placeholder="Additional remarks, payment notes, or instructions..."
                  formik={formik}
                  name="remarks"
                  new_width="w-full"
                />
              </div>

              {/* Right Column: Financial Summary Card */}
              <div className="lg:col-span-5 bg-gray-50/80 border border-gray-200/80 rounded-xl p-4 space-y-3">
                <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider border-b border-gray-200 pb-2">
                  Financial Summary
                </h4>

                <div className="flex justify-between items-center text-xs text-gray-600">
                  <span className="font-medium">Subtotal</span>
                  <span className="font-bold text-gray-800">
                    <NuAmount currency={selectedCurrency} value={subTotal} />
                  </span>
                </div>

                <div className="flex justify-between items-center text-xs">
                  <span className="font-medium text-gray-600">Discount Amount</span>
                  <div className="w-32">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formik.values.discountAmount}
                      onChange={(e) => {
                        formik.setFieldValue("discountAmount", e.target.value);
                        setFormValues((prev) => ({ ...prev, discountAmount: e.target.value }));
                      }}
                      placeholder="0.00"
                      className="w-full h-8 px-2 text-xs font-semibold text-right border border-gray-200 rounded-lg outline-none focus:border-emerald-500 bg-white"
                    />
                  </div>
                </div>

                <div className="flex justify-between items-center text-xs">
                  <span className="font-medium text-gray-600">Transportation Charges</span>
                  <div className="w-32">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formik.values.transportationCharges}
                      onChange={(e) => {
                        formik.setFieldValue("transportationCharges", e.target.value);
                        setFormValues((prev) => ({ ...prev, transportationCharges: e.target.value }));
                      }}
                      placeholder="0.00"
                      className="w-full h-8 px-2 text-xs font-semibold text-right border border-gray-200 rounded-lg outline-none focus:border-emerald-500 bg-white"
                    />
                  </div>
                </div>

                <div className="flex justify-between items-center text-xs text-gray-600">
                  <label htmlFor="vatApplicable" className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      id="vatApplicable"
                      checked={formik.values.vatApplicable}
                      onChange={(e) => {
                        formik.setFieldValue("vatApplicable", e.target.checked);
                        setFormValues((prev) => ({ ...prev, vatApplicable: e.target.checked }));
                      }}
                      className="h-4 w-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500 cursor-pointer"
                    />
                    <span className="font-medium text-gray-700">Apply VAT ({vatRate}%)</span>
                  </label>
                  <span className="font-bold text-gray-800">
                    <NuAmount currency={selectedCurrency} value={vatAmount} />
                  </span>
                </div>

                <div className="border-t border-gray-200 pt-3 flex justify-between items-center text-sm font-bold text-emerald-800">
                  <span>Grand Total</span>
                  <span className="text-base">
                    <NuAmount currency={selectedCurrency} value={grandTotal} />
                  </span>
                </div>
              </div>

            </div>

          </div>

          {/* Form Footer Actions */}
          <div className="p-3 bg-white border-t border-gray flex justify-end items-center gap-2">
            <CancelButton handleClick={() => Oncancel(fromUrl, { currentPage: pageIndex, searchQuery })} />
            <SubmitButton name="Save" isLoading={isLoading} />
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditProformaInvoice;
